import React from 'react';
import {
  Box,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../../../common/library/Button/Button';
import { useIsMobile, useIsSmallScreen } from '../../../../common/hooks/useResponsive';
import { TestIds } from '../../../../assets/testIds';
import { useAppSelector } from '../../../../store/hooks';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { isPaidTier } from '../../../../common/utils/MembershipTierAccess';
import { useServiceNationUrl } from '../../../../common/utils/useServiceNationUrl';
import { AccessResource, useHasAccess } from '../../../../common/hooks/useHasAccess';
import { usePartnerExternalEnrollmentForms } from './PartnerSpecificFormConfig';

const REBATE_ANALYSIS_URL = 'https://servicenation.formstack.com/forms/rebate_program_questionnaire';

export function PartnersHeader() {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isSmallScreen = useIsSmallScreen();
  const memberInformation = useAppSelector(selectMemberInformation);
  const subscriptionTier = memberInformation?.company?.subscription?.subscriptionTier;
  // Association pays, so it gets the paid buttons: `isPaidTier` reads the shared
  // paid/free chart rather than `isFreeMembershipTier`, which counts Association as
  // free and needed an AM special case here (SN-996).
  const showPaidButtons = isPaidTier(subscriptionTier);
  const hasReportPurchasesAccess = useHasAccess(AccessResource.ReportPurchases);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { openExternalLink } = useNavigationUtils();
  const { buildServiceNationUrl } = useServiceNationUrl();
  const externalEnrollmentForms = usePartnerExternalEnrollmentForms();
  const showRequiredForms =
    !!memberInformation?.id && Object.keys(externalEnrollmentForms).length > 0;

  const handleOpenRequiredForms = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseRequiredForms = () => {
    setAnchorEl(null);
  };

  const handleExternalLinkClick = async (url: string | undefined, ssoDestination?: string) => {
    if (ssoDestination) {
      try {
        openExternalLink(await buildServiceNationUrl(ssoDestination));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to generate Service Nation SSO token', error);
      }
    } else if (url) {
      openExternalLink(url);
    } else {
      // eslint-disable-next-line no-console
      console.error('No URL or SSO destination found for required form');
    }
  };

  const handleRequiredFormClick = async (url: string | undefined, ssoDestination?: string) => {
    handleCloseRequiredForms();
    handleExternalLinkClick(url, ssoDestination);
  };

  const handleRebateAnalysisClick = () => {
    openExternalLink(REBATE_ANALYSIS_URL);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isSmallScreen ? 'flex-start' : 'center',
        width: '100%',
        marginBottom: isMobile ? theme.spacing(1) : theme.spacing(5),
        gap: theme.spacing(2),
      }}
    >
      <Typography variant="h12">
        {t('navigation.partners')}
      </Typography>
      {showRequiredForms && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(1.5),
            alignItems: 'center',
            // Wrap rather than clip: three buttons plus the page title do not fit
            // on one row on a phone, which used to push "Purchase reporting" off
            // the right edge where it was unreachable.
            flexWrap: 'wrap',
            ...(isSmallScreen && {
              width: '100%',
              justifyContent: 'flex-start',
            }),
          }}
        >
          <OptimusButton
            size="m"
            customVariant="secondaryGray"
            fullWidth={false}
            leadingIcon={<PlaylistAddCheckOutlinedIcon />}
            trailingIcon={
              open ?
                <KeyboardArrowDownOutlinedIcon /> :
                <KeyboardArrowRightOutlinedIcon />
            }
            onClick={handleOpenRequiredForms}
            sx={{
              whiteSpace: 'nowrap',
            }}
            buttonText={t('partners.requiredForms') || ''}
            data-testid={TestIds.partnersPage.requiredFormsButton}
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseRequiredForms}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            MenuListProps={{ 'aria-label': t('partners.requiredForms') || 'Required forms' }}
            PaperProps={{
              sx: {
                mt: theme.spacing(0.5),
                minWidth: theme.spacing(25),
                maxHeight: theme.spacing(52.5),
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1),
                boxShadow: `0px 1.25px 2.5px 0px ${theme.palette.effects.shadowXs}`,
              },
            }}
          >
            {Object.entries(externalEnrollmentForms).map(([code, config]) => (
              <MenuItem
                key={code}
                onClick={() => handleRequiredFormClick(
                  config.externalEnrollmentForm,
                  config.ssoDestination,
                )}
                dense
              >
                <ListItemText
                  primary={config.partnerName}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: theme.spacing(1.75),
                      fontWeight: 400,
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
          {showPaidButtons && (
            <OptimusButton
              size="m"
              customVariant="secondaryGray"
              fullWidth={false}
              leadingIcon={<ShowChartIcon />}
              onClick={handleRebateAnalysisClick}
              sx={{
                whiteSpace: 'nowrap',
              }}
              buttonText={t('partners.rebateAnalysis') || ''}
              data-testid={TestIds.partnersPage.rebateAnalysisButton}
            />
          )}
          {/* SN-190: gated on its own SN grant, not tier - a free/AM-tier SN
              account can still be granted my_purchases_access. */}
          {hasReportPurchasesAccess &&
            <OptimusButton
              size="m"
              customVariant="secondaryGray"
              fullWidth={false}
              leadingIcon={<SummarizeOutlinedIcon />}
              onClick={() => handleExternalLinkClick(undefined, 'purchase-reporting')}
              sx={{
                whiteSpace: 'nowrap',
              }}
              buttonText={t('partners.purchaseReporting') || ''}
              data-testid={TestIds.partnersPage.purchaseReportingButton}
            />}
        </Box>
      )}
    </Box>
  );
}
